import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Tooltip } from '@mui/material';
import { AutocompleteValueField } from 'components/form';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
  GET_GLOBAL_EQUIPMENT_OPTIONS,
  GlobalEquipmentOptionsResponse,
  GlobalEquipmentOptionsVariables,
} from '../../api';
import { equipmentStandardInfoSchema } from '../../model';

type StandardInfoFormValues = {
  equipment: z.infer<typeof equipmentStandardInfoSchema>;
};

export const StandardInfo = () => {
  const { control, setValue } = useFormContext<StandardInfoFormValues>();
  const [make = [], year = []] = useWatch({ control, name: ['equipment.make', 'equipment.year'] });

  const { data, previousData, loading } = useQuery<GlobalEquipmentOptionsResponse, GlobalEquipmentOptionsVariables>(
    GET_GLOBAL_EQUIPMENT_OPTIONS,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        input: { make, year },
      },
    }
  );

  const source = data ?? previousData;

  const { makeOptions, modelOptions, yearOptions } = useMemo(
    () => ({
      makeOptions: source?.globalEquipment?.result?.entity?.makes ?? [],
      modelOptions: source?.globalEquipment?.result?.entity?.models ?? [],
      yearOptions: source?.globalEquipment?.result?.entity?.years ?? [],
    }),
    [source?.globalEquipment?.result?.entity]
  );

  return (
    <>
      <AutocompleteValueField
        multiple
        size="small"
        control={control}
        loading={loading}
        name="equipment.year"
        options={yearOptions}
        disableCloseOnSelect
        textFieldProps={{
          label: 'Year',
        }}
        extraAction={() => {
          setValue('equipment.make', []);
          setValue('equipment.model', []);
        }}
      />

      <AutocompleteValueField
        multiple
        size="small"
        control={control}
        loading={loading}
        name="equipment.make"
        options={makeOptions}
        disableCloseOnSelect
        textFieldProps={{
          label: 'Make',
        }}
        extraAction={() => {
          setValue('equipment.model', []);
        }}
      />

      <Tooltip arrow title={!make.length ? 'Please select a make first' : ''}>
        <Box>
          <AutocompleteValueField
            multiple
            size="small"
            control={control}
            loading={loading}
            disableCloseOnSelect
            name="equipment.model"
            options={modelOptions}
            disabled={!make.length}
            textFieldProps={{
              label: 'Model',
            }}
          />
        </Box>
      </Tooltip>
    </>
  );
};
